import { Component, Inject, Injector, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Note } from 'src/app/models/note.model';
import { Lookup } from 'src/app/models/lookup.model';
import { NoteType } from 'src/app/enums/noteType.enum';
import { NoteService } from 'src/app/services/note.service';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { ButtonFormField, DateTimeFormField, SingleSelectionFormField, TextAreaFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';

export interface INoteDialogData extends IFormDialogData {
  fromDate: number | Date | string;
  toDate: number | Date | string;
  taskAssignmentOptions: Lookup[];
}

interface NoteFormModel extends Note {
  fromDateValue?: Date;
  toDateValue?: Date;
}

export interface INoteDialogResult {
  note: Note;
  file: File | null;
}

const ALLOWED_FILE_TYPES: Partial<Record<NoteType, string[]>> = {
  [NoteType.Voice]: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/webm'],
  [NoteType.Video]: ['video/mp4', 'video/webm', 'video/quicktime'],
};

@Component({
  standalone: false,
  selector: 'app-notes-dialog',
  templateUrl: './../../shared/form/form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/form/form.component.scss'],
})
export class NotesDialogComponent extends FormComponent<NoteFormModel> implements OnInit {
  selectedFile: File | null = null;

  constructor(
    dialogRef: MatDialogRef<NotesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: INoteDialogData,
    noteService: NoteService,
    protected injector: Injector,
    private renderer: Renderer2
  ) {
    super(dialogRef, noteService, data, 'Note', injector);
    this.setMainButtonClick(() => this.handleSave());
  }

  ngOnInit(): void {
    this.getEntity().subscribe(entity => {
      this.entity = entity;
      this.initModel();
      this.initFields();
    });
  }

  initModel(): void {
    const fromDate = this.normalizeDateValue(this.data.fromDate);
    const toDate = this.normalizeDateValue(this.data.toDate);

    if (fromDate) {
      this.entity.fromDateValue = fromDate;
    }
    if (toDate) {
      this.entity.toDateValue = toDate;
    }
    this.entity.type = NoteType.Text;
  }

  initFields(): void {
    this.fields = [
      new DateTimeFormField({
        property: nameOf<NoteFormModel>(x => x.fromDateValue),
        name: this.translateService.getDynamicTranslation('From Date'),
        columnSpan: new ColumnSpan(1, 2),
      }),
      new DateTimeFormField({
        property: nameOf<NoteFormModel>(x => x.toDateValue),
        name: this.translateService.getDynamicTranslation('To Date'),
        columnSpan: new ColumnSpan(1, 2),
      }),
      new SingleSelectionFormField({
        property: nameOf<NoteFormModel>(x => x.type),
        name: this.translateService.getDynamicTranslation('Type'),
        columnSpan: new ColumnSpan(1, 2),
        options: [
          { id: NoteType.Text, name: this.translateService.getDynamicTranslation('Text') } as Lookup,
          { id: NoteType.Voice, name: this.translateService.getDynamicTranslation('Voice') } as Lookup,
          { id: NoteType.Video, name: this.translateService.getDynamicTranslation('Video') } as Lookup,
        ],
        onChange: () => {
          this.selectedFile = null;
          this.updateFieldVisibility();
        },
      }),
      new SingleSelectionFormField({
        property: nameOf<NoteFormModel>(x => x.taskAssignmentId),
        name: this.translateService.getDynamicTranslation('Task Assignment'),
        columnSpan: new ColumnSpan(1, 2),
        required: false,
        options: this.data.taskAssignmentOptions ?? [],
      }),
      new ButtonFormField({
        property: nameOf<NoteFormModel>(x => x.fileName),
        name: this.translateService.getDynamicTranslation('File'),
        columnSpan: new ColumnSpan(1, 2),
        required: false,
        isVisible: (entity: NoteFormModel) => entity?.type !== NoteType.Text,
        additionalLabel: () => this.selectedFile
          ? this.selectedFile.name
          : this.translateService.getDynamicTranslation('Please select file to upload'),
        onClick: () => this.pickFile(),
      }),
      new TextAreaFormField({
        property: nameOf<NoteFormModel>(x => x.text),
        name: this.translateService.getDynamicTranslation('Notes Text'),
        columnSpan: new ColumnSpan(1, 1),
      }),
    ];
  }

  private pickFile(): void {
    const input: HTMLInputElement = this.renderer.createElement('input');
    input.type = 'file';
    if (this.entity.type === NoteType.Voice) {
      input.accept = 'audio/*';
    } else if (this.entity.type === NoteType.Video) {
      input.accept = 'video/*';
    }
    input.addEventListener('change', () => {
      const file = input.files?.[0] ?? null;
      const allowedTypes = ALLOWED_FILE_TYPES[this.entity.type];
      if (file && allowedTypes && !allowedTypes.includes(file.type)) {
        this.toastService.displayErrorMessage('Invalid file type selected', true);
        return;
      }
      this.selectedFile = file;
      this.updateFieldVisibility();
    });
    input.click();
  }

  private normalizeDateValue(value: number | Date | string): Date | undefined {
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'number') {
      return this.dateFormatService.convertTimestampToDate(value);
    }
    if (typeof value === 'string') {
      return this.dateFormatService.convertStringToDate(value);
    }
    return undefined;
  }

  getNewObject(): NoteFormModel {
    return new Note() as NoteFormModel;
  }

  validate(): boolean {
    const fromDate = this.entity.fromDateValue;
    const toDate = this.entity.toDateValue;

    if (fromDate && toDate && toDate < fromDate) {
      this.toastService.displayErrorMessage('End Date cannot be before Start Date', true);
      return false;
    }

    if (this.entity.type !== NoteType.Text && !this.selectedFile) {
      this.toastService.showWarning('Please select file to upload', true);
      return false;
    }

    return true;
  }

  private handleSave(): void {
    if (!this.validate()) {
      return;
    }

    const model = JSON.parse(JSON.stringify(this.entity)) as NoteFormModel;
    const note = new Note();
    note.text = model.text;
    note.type = model.type;
    note.taskAssignmentId = model.taskAssignmentId;
    note.timestampStart = model.fromDateValue
      ? this.dateFormatService.convertDateToTimestamp(this.normalizeDateValue(model.fromDateValue))
      : undefined;
    note.timestampEnd = model.toDateValue
      ? this.dateFormatService.convertDateToTimestamp(this.normalizeDateValue(model.toDateValue))
      : undefined;

    const result: INoteDialogResult = { note, file: this.selectedFile };
    this.dialogRef.close(result);
  }
}
